import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import {
    Card,
    Button,
    DatePicker,
    Dropdown,
    Space,
    Select,
    Avatar,
    Tag,
    Image,
    MenuProps
} from "antd";
import { CalendarOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { useState } from "react";
import dayjs from "dayjs";
import InteractionChart from "./InteractionChart";
import StaffCharts from "./StaffCharts";
import Layout from '../../Layout'
import DetailedReportTable from "./DetailedReportTable";

const { RangePicker } = DatePicker;

const pageOptions = [
    {
        value: "buso",
        label: "Buso",
        email: "landingPage@buso",
        avatar: "https://i.pravatar.cc/40?img=1",
    },
    {
        value: "huanpc",
        label: "Huanpc",
        email: "84909506018@s.whatsapp.net",
        avatar: "",
    },
    {
        value: "cms",
        label: "CMS",
        email: "",
        icon: true,
    },
];

export default function OverviewReport() {
  const { t } = useTranslation();
    const [page, setPage] = useState();
    const [group, setGroup] = useState();
    const [value, setValue] = useState<string[]>([]);

    const [range, setRange] = useState([
        dayjs().subtract(6, "day"),
        dayjs()
    ]);

    const quickSelect = (type: string) => {
        switch (type) {
            case "today":
                setRange([dayjs(), dayjs()]);
                break;

            case "yesterday":
                setRange([
                    dayjs().subtract(1, "day"),
                    dayjs().subtract(1, "day")
                ]);
                break;

            case "7days":
                setRange([
                    dayjs().subtract(6, "day"),
                    dayjs()
                ]);
                break;

            case "30days":
                setRange([
                    dayjs().subtract(29, "day"),
                    dayjs()
                ]);
                break;
        }
    };

    const dateDropdown = (
        <div className="bg-white dark:bg-[#1f1f1f] p-4 rounded-lg w-[320px] shadow-lg">

            <p className="text-sm font-semibold mb-2 text-gray-700 dark:text-gray-200">
                {t('page_report.quick_select')}
            </p>

            <div className="grid grid-cols-2 gap-2 mb-3">
                <Button onClick={() => quickSelect("today")}>{t('page_report.today')}</Button>
                <Button onClick={() => quickSelect("yesterday")}>{t('page_report.yesterday')}</Button>
                <Button onClick={() => quickSelect("7days")}>{t('page_report.last_7_days')}</Button>
                <Button onClick={() => quickSelect("30days")}>{t('page_report.last_30_days')}</Button>
            </div>

            <p className="text-sm font-semibold mb-2 text-gray-700 dark:text-gray-200">
                {t('page_report.custom')}
            </p>

            <RangePicker
                value={range as any}
                onChange={(val) => setRange(val as any)}
                className="w-full"
            />
        </div>
    );

    return (
        <Layout>
            <Helmet>
                <title>{t('page.page_report')} | Khothi</title>
            </Helmet>
            <div className="space-y-6">

                {/* HEADER */}

                <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                        {t('page_report.overview_report')}
                    </h2>
                    <p className="text-gray-500 dark:text-gray-400">
                        {t('page_report.overview_subtitle')}
                    </p>
                </div>

                {/* FILTER */}

                <div className="flex flex-wrap gap-3">
                    {/* Page group */}

                    <Select
                        value={group}
                        placeholder={t('page_report.select_page_group')}
                        className="w-[220px]"
                        options={[
                            { label: t('page_report.all'), value: "all" },
                            { label: t('page_report.group_1'), value: "g1" },
                            { label: t('page_report.group_2'), value: "g2" },
                        ]}
                        popupClassName="dark:bg-[#1f1f1f]"
                    />

                    {/* Page select */}

                    <Select
                        mode="multiple"
                        value={value}
                        onChange={setValue}
                        placeholder={t('page_report.select_page')}
                        showSearch
                        suffixIcon={<SearchOutlined />}
                        className="w-[260px]"
                        popupClassName="dark:bg-[#1f1f1f]"
                        options={pageOptions}
                        optionFilterProp="label"

                        /* Render dropdown item */

                        optionRender={(option) => {
                            const data: any = option.data;

                            return (
                                <div className="flex items-center gap-3 py-1">

                                    {data.icon ? (
                                        <Avatar style={{ background: "#1890ff" }}>👤</Avatar>
                                    ) : (
                                        <Avatar src={data.avatar}>
                                            {data.label[0]}
                                        </Avatar>
                                    )}

                                    <div className="flex flex-col">
                                        <span className="text-gray-800 dark:text-gray-200">
                                            {data.label}
                                        </span>

                                        {data.email && (
                                            <span className="text-xs text-gray-500">
                                                {data.email}
                                            </span>
                                        )}
                                    </div>

                                </div>
                            );
                        }}

                        /* Avatar khi đã chọn */

                        tagRender={(props) => {
                            const { value, closable, onClose } = props;

                            const pageItem = pageOptions.find((p) => p.value === value);

                            return (
                                <Avatar
                                    src={pageItem?.avatar}
                                    size={20}
                                    className="mr-1 cursor-pointer"
                                    onClick={onClose}
                                >
                                    {pageItem?.label[0]}
                                </Avatar>
                            );
                        }}
                    />

                    <Dropdown dropdownRender={() => dateDropdown} trigger={["click"]}>
                        <Button icon={<CalendarOutlined />}>
                            {range[0].format("YYYY-MM-DD")} → {range[1].format("YYYY-MM-DD")}
                        </Button>
                    </Dropdown>

                    <Button icon={<ReloadOutlined />}>
                        {t('page_report.reset_filter')}
                    </Button>

                </div>

                {/* THỐNG KÊ */}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

                    <Card className="dark:bg-[#1f1f1f] dark:border-[#303030]">

                        <p className="text-gray-500 dark:text-gray-400 text-sm">
                            {t('page_report.total_new_customers')}
                        </p>

                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                            3
                        </h2>

                    </Card>

                    <Card className="dark:bg-[#1f1f1f] dark:border-[#303030]">

                        <p className="text-gray-500 dark:text-gray-400 text-sm">
                            {t('page_report.total_comments')}
                        </p>

                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                            3
                        </h2>

                    </Card>

                    <Card className="dark:bg-[#1f1f1f] dark:border-[#303030]">

                        <p className="text-gray-500 dark:text-gray-400 text-sm">
                            {t('page_report.total_messages')}
                        </p>

                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                            3
                        </h2>

                    </Card>

                    <Card className="dark:bg-[#1f1f1f] dark:border-[#303030]">

                        <p className="text-gray-500 dark:text-gray-400 text-sm">
                            {t('page_report.total_orders')}
                        </p>

                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                            20
                        </h2>

                    </Card>

                </div>

                {/* CHART */}

                <Card className="dark:bg-[#1f1f1f] dark:border-[#303030]">
                    <h2 className="text-lg text-gray-900 font-semibold t-text-white mb-3">
                        {t('page_report.chart_by_day')}
                    </h2>
                    <InteractionChart />

                </Card>

                <DetailedReportTable />

            </div>
        </Layout>
    );
}